import React, { useEffect, useState } from "react";
import { Modal, Tabs, Table, Spin, Alert } from "antd";
import type { TabsProps } from "antd";

interface CustomerDetailModalProps {
  open: boolean;
  onClose: () => void;
  idClient: string;
  customerName: string;
  year?: number;
  quarter?: number;
  dateFrom?: string;
  dateTo?: string;
}

const getColumns = (data: any[]) => {
  if (!data || data.length === 0) return [];
  return Object.keys(data[0]).map((key) => ({
    title: key,
    dataIndex: key,
    key,
    ellipsis: true,
    width: 180,
  }));
};

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({
  open,
  onClose,
  idClient,
  customerName,
  year,
  quarter,
  dateFrom,
  dateTo,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pagination state for each table
  const [qbdInvPage, setQbdInvPage] = useState(1);
  const [qbdInvPageSize, setQbdInvPageSize] = useState(10);
  const [qbdPayPage, setQbdPayPage] = useState(1);
  const [qbdPayPageSize, setQbdPayPageSize] = useState(10);
  const [qbdCredPage, setQbdCredPage] = useState(1);
  const [qbdCredPageSize, setQbdCredPageSize] = useState(10);
  const [qboInvPage, setQboInvPage] = useState(1);
  const [qboInvPageSize, setQboInvPageSize] = useState(10);
  const [qboPayPage, setQboPayPage] = useState(1);
  const [qboPayPageSize, setQboPayPageSize] = useState(10);
  const [qboCredPage, setQboCredPage] = useState(1);
  const [qboCredPageSize, setQboCredPageSize] = useState(10);

  const [qbd, setQbd] = useState<any>({
    invoices: [],
    invoicesTotal: 0,
    payments: [],
    paymentsTotal: 0,
    credits: [],
    creditsTotal: 0,
  });
  const [qbo, setQbo] = useState<any>({
    invoices: [],
    invoicesTotal: 0,
    payments: [],
    paymentsTotal: 0,
    credits: [],
    creditsTotal: 0,
  });

  // Helper to build query params for fetch
  const buildParams = () => {
    return new URLSearchParams({
      idClient,
      customerName,
      ...(year ? { year: year.toString() } : {}),
      ...(quarter ? { quarter: quarter.toString() } : {}),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      invPage: qbdInvPage.toString(),
      invPageSize: qbdInvPageSize.toString(),
      payPage: qbdPayPage.toString(),
      payPageSize: qbdPayPageSize.toString(),
      credPage: qbdCredPage.toString(),
      credPageSize: qbdCredPageSize.toString(),
      qboInvPage: qboInvPage.toString(),
      qboInvPageSize: qboInvPageSize.toString(),
      qboPayPage: qboPayPage.toString(),
      qboPayPageSize: qboPayPageSize.toString(),
      qboCredPage: qboCredPage.toString(),
      qboCredPageSize: qboCredPageSize.toString(),
      qbo_origin: process.env.REACT_APP_QBO_ORIGIN || "",
    });
  };

  // Fetch data when modal opens or pagination/filter changes
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    fetch(
      `${process.env.REACT_APP_DEV_API}/qbd/qbo-customer-detail?` +
        buildParams(),
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("info")}`,
        },
      },
    )
      .then((res) => res.json())
      .then((result) => {
        if (!result.success) throw new Error("Failed to fetch details");
        const safeQbd = result.qbd || {};
        const safeQbo = result.qbo || {};
        setQbd({
          invoices: Array.isArray(safeQbd.invoices) ? safeQbd.invoices : [],
          invoicesTotal:
            typeof safeQbd.invoicesTotal === "number"
              ? safeQbd.invoicesTotal
              : 0,
          payments: Array.isArray(safeQbd.payments) ? safeQbd.payments : [],
          paymentsTotal:
            typeof safeQbd.paymentsTotal === "number"
              ? safeQbd.paymentsTotal
              : 0,
          credits: Array.isArray(safeQbd.credits) ? safeQbd.credits : [],
          creditsTotal:
            typeof safeQbd.creditsTotal === "number" ? safeQbd.creditsTotal : 0,
        });
        setQbo({
          invoices: Array.isArray(safeQbo.invoices) ? safeQbo.invoices : [],
          invoicesTotal:
            typeof safeQbo.invoicesTotal === "number"
              ? safeQbo.invoicesTotal
              : 0,
          payments: Array.isArray(safeQbo.payments) ? safeQbo.payments : [],
          paymentsTotal:
            typeof safeQbo.paymentsTotal === "number"
              ? safeQbo.paymentsTotal
              : 0,
          credits: Array.isArray(safeQbo.credits) ? safeQbo.credits : [],
          creditsTotal:
            typeof safeQbo.creditsTotal === "number" ? safeQbo.creditsTotal : 0,
        });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [
    open,
    idClient,
    customerName,
    year,
    quarter,
    dateFrom,
    dateTo,
    qbdInvPage,
    qbdInvPageSize,
    qbdPayPage,
    qbdPayPageSize,
    qbdCredPage,
    qbdCredPageSize,
    qboInvPage,
    qboInvPageSize,
    qboPayPage,
    qboPayPageSize,
    qboCredPage,
    qboCredPageSize,
  ]);

  const items: TabsProps["items"] = [
    {
      key: "qbd-invoices",
      label: "QBD Invoices",
      children: (
        <Table
          columns={getColumns(qbd.invoices)}
          dataSource={qbd.invoices}
          size="small"
          scroll={{ x: true }}
          pagination={{
            current: qbdInvPage,
            pageSize: qbdInvPageSize,
            total: qbd.invoicesTotal,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              setQbdInvPage(page);
              setQbdInvPageSize(pageSize);
            },
          }}
        />
      ),
    },
    {
      key: "qbd-payments",
      label: "QBD Payments",
      children: (
        <Table
          columns={getColumns(qbd.payments)}
          dataSource={qbd.payments}
          size="small"
          scroll={{ x: true }}
          pagination={{
            current: qbdPayPage,
            pageSize: qbdPayPageSize,
            total: qbd.paymentsTotal,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              setQbdPayPage(page);
              setQbdPayPageSize(pageSize);
            },
          }}
        />
      ),
    },
    {
      key: "qbd-credits",
      label: "QBD Credits",
      children: (
        <Table
          columns={getColumns(qbd.credits)}
          dataSource={qbd.credits}
          size="small"
          scroll={{ x: true }}
          pagination={{
            current: qbdCredPage,
            pageSize: qbdCredPageSize,
            total: qbd.creditsTotal,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              setQbdCredPage(page);
              setQbdCredPageSize(pageSize);
            },
          }}
        />
      ),
    },
    {
      key: "qbo-invoices",
      label: "QBO Invoices",
      children: (
        <Table
          columns={getColumns(qbo.invoices)}
          dataSource={qbo.invoices}
          size="small"
          scroll={{ x: true }}
          pagination={{
            current: qboInvPage,
            pageSize: qboInvPageSize,
            total: qbo.invoicesTotal,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              setQboInvPage(page);
              setQboInvPageSize(pageSize);
            },
          }}
        />
      ),
    },
    {
      key: "qbo-payments",
      label: "QBO Payments",
      children: (
        <Table
          columns={getColumns(qbo.payments)}
          dataSource={qbo.payments}
          size="small"
          scroll={{ x: true }}
          pagination={{
            current: qboPayPage,
            pageSize: qboPayPageSize,
            total: qbo.paymentsTotal,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              setQboPayPage(page);
              setQboPayPageSize(pageSize);
            },
          }}
        />
      ),
    },
    {
      key: "qbo-credits",
      label: "QBO Credits",
      children: (
        <Table
          columns={getColumns(qbo.credits)}
          dataSource={qbo.credits}
          size="small"
          scroll={{ x: true }}
          pagination={{
            current: qboCredPage,
            pageSize: qboCredPageSize,
            total: qbo.creditsTotal,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              setQboCredPage(page);
              setQboCredPageSize(pageSize);
            },
          }}
        />
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onClose}
      width={1200}
      title={`Customer Details: ${customerName}`}
      footer={null}
      destroyOnClose
      bodyStyle={{ padding: 32 }}
    >
      {loading ? (
        <Spin size="large" />
      ) : error ? (
        <Alert type="error" message={error} />
      ) : (
        <Tabs items={items} />
      )}
    </Modal>
  );
};

export default CustomerDetailModal;
